import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import useSessionStorage from '@afosto/utils-react/useSessionStorage';
import usePrevious from '@afosto/utils-react/component/usePrevious';
import isEqual from 'lodash.isequal';
import combineReducers from '@afosto/utils-react/combineReducers';
import wrapWithDispatch from '@afosto/utils-react/wrapWithDispatch';
import wrapWithState from '@afosto/utils-react/wrapWithState';
import parseUrlQuery from '@afosto/utils/parseUrlQuery';
import createDataSourceObject from './helpers/createDataSourceObject';
import createDataSourceUrl from './helpers/createDataSourceUrl';
import availableModules from './modules';

/**
 * The default settings for the data source hook.
 * @type {object}
 */
const DATA_SOURCE_DEFAULT_SETTINGS = {
  dataSourceUrl: false,
  dataSourceUrlKey: 's',
  dataSourceTimerTimeout: 200,
  isLoadingData: undefined,
  storeStateKey: undefined,
};

/**
 * Initialize the provided modules.
 * @param {array} modules
 * @returns {object}
 */
const initializeModules = modules =>
  modules.reduce(
    (acc, moduleKey) => {
      const module = availableModules[moduleKey];
      const { reducer, state, actions, helpers } = module;

      return {
        reducers: { ...acc.reducers, [moduleKey]: reducer },
        actions: { ...acc.actions, [moduleKey]: { ...actions } },
        helpers: { ...acc.helpers, [moduleKey]: { ...helpers } },
        state: { ...acc.state, [moduleKey]: { ...state } },
      };
    },
    {
      reducers: {},
      actions: {},
      helpers: {},
      state: {},
    },
  );

/**
 * Initialize the modules state.
 * @param {array} activeModules
 * @param {array} registeredModules
 * @param {object} state
 * @returns {object}
 */
const initializeModulesState = (activeModules, registeredModules, state = {}) =>
  activeModules.reduce((acc, moduleKey) => {
    const registeredState = registeredModules.state[moduleKey] || {};
    const providedModuleState = state[moduleKey] || {};
    return { ...acc, [moduleKey]: { ...registeredState, ...providedModuleState } };
  }, {});

/**
 * useDataSource hook.
 * @param {array|function} dataSource
 * @param {array} modules
 * @param {object} options
 * @param {any} providedState
 * @returns {object}
 */
export default (dataSource, { modules = [], options = {}, ...providedState } = {}) => {
  const moduleString = modules.join(',');
  const [activeModules, registeredModules] = useMemo(() => {
    const providedModules = moduleString.split(',').filter(key => !!availableModules[key]);
    return [providedModules, initializeModules(providedModules)];
  }, [moduleString]);
  const modulesState = initializeModulesState(activeModules, registeredModules, providedState);
  const dataSourceReducer = combineReducers(registeredModules.reducers);
  const [dataSourceState, dispatch] = useReducer(dataSourceReducer, modulesState);
  const actions = useMemo(
    () =>
      activeModules.reduce(
        (acc, moduleKey) => ({
          ...acc,
          [moduleKey]: wrapWithDispatch(dispatch, registeredModules.actions[moduleKey]),
        }),
        {},
      ),
    [activeModules, registeredModules, dispatch],
  );
  const helpers = useMemo(
    () =>
      activeModules.reduce(
        (acc, moduleKey) => ({
          ...acc,
          [moduleKey]: wrapWithState(
            { ...dataSourceState[moduleKey], ...actions[moduleKey] },
            registeredModules.helpers[moduleKey],
          ),
        }),
        {},
      ),
    [activeModules, registeredModules, dataSourceState, actions],
  );
  const combinedState = activeModules.reduce(
    (acc, moduleKey) => ({
      ...acc,
      [moduleKey]: { ...dataSourceState[moduleKey], ...actions[moduleKey], ...helpers[moduleKey] },
    }),
    {},
  );
  const [getStorage, setStorage] = useSessionStorage();
  const [settings, setSettings] = useState({ ...DATA_SOURCE_DEFAULT_SETTINGS, ...options });
  const [lastStoredView, setLastStoredView] = useState(null);
  const [data, setData] = useState([]);
  const [dataTimer, setDataTimer] = useState(null);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [viewsInitialized, setViewsInitialized] = useState(false);
  const dataSourceRef = useRef(dataSource);
  const combinedStateRef = useRef(combinedState);
  const dataTimerRef = useRef(dataTimer);
  const providedStateRef = useRef();
  const actionsRef = useRef(actions);
  const settingsRef = useRef();
  const urlRef = useRef(window.location.search);

  const {
    dataSourceUrl = DATA_SOURCE_DEFAULT_SETTINGS.dataSourceUrl,
    dataSourceUrlKey = DATA_SOURCE_DEFAULT_SETTINGS.dataSourceUrlKey,
    dataSourceTimerTimeout = DATA_SOURCE_DEFAULT_SETTINGS.dataSourceTimerTimeout,
    isLoadingData: settingsIsLoadingData = DATA_SOURCE_DEFAULT_SETTINGS.isLoadingData,
    storeStateKey = DATA_SOURCE_DEFAULT_SETTINGS.storeStateKey,
  } = settings;

  const {
    pagination: paginationModule = {},
    filters: filtersModule = {},
    sorting: sortingModule = {},
    dataProperties: dataPropertiesModule = {},
    selection: selectionModule = {},
    views: viewsModule = {},
    search: searchModule = {},
  } = combinedState;
  const { activePage, itemsPerPage } = paginationModule;
  const { activeFilters, filters, fixedFilters } = filtersModule;
  const { activeSort } = sortingModule;
  const { selectedProperties } = dataPropertiesModule;
  const { defaultView, activeView } = viewsModule;
  const { selectedItems = [] } = selectionModule;
  const { searchValue = '', isFuzzy = true } = searchModule;
  const activeViewRef = useRef(activeView);
  const selectedItemsRef = useRef(selectedItems);
  const dataRef = useRef(data);
  const latestStateRef = useRef(combinedState);

  const previousActiveView = usePrevious(activeView);
  const previousOptions = usePrevious(options);
  const previousStoreStateKey = usePrevious(storeStateKey);
  const previousProvidedState = usePrevious(providedState);
  const previousProvidedProperties = previousProvidedState?.dataProperties?.properties;
  const previousProvidedFixedProperties = previousProvidedState?.dataProperties?.fixedProperties;
  const previousProvidedFilters = previousProvidedState?.filters?.filters;
  const previousProvidedFixedFilters = previousProvidedState?.filters?.fixedFilters;
  const previousProvidedSortOptions = previousProvidedState?.sorting?.sortOptions;
  const previousProvidedViews = previousProvidedState?.views?.views;
  const previousProvidedFixedViews = previousProvidedState?.views?.fixedViews;

  const {
    filters: providedFiltersModule = {},
    dataProperties: providedDataPropertiesModule = {},
    sorting: providedSortingModule = {},
    views: providedViewsModule = {},
  } = providedState;
  const {
    loadFilterOptions: providedLoadFilterOptions,
    filters: providedFilters,
    fixedFilters: providedFixedFilters,
  } = providedFiltersModule;
  const {
    fixedProperties: providedFixedProperties,
    properties: providedDataProperties,
  } = providedDataPropertiesModule;
  const { sortOptions: providedSortOptions } = providedSortingModule;
  const { fixedViews: providedFixedViews, views: providedViews } = providedViewsModule;
  const providedLoadFilterOptionsRef = useRef(providedLoadFilterOptions);
  const latestProvidedStateRef = useRef(providedState);

  useEffect(() => {
    settingsRef.current = settings;
    latestStateRef.current = combinedState;
    latestProvidedStateRef.current = providedState;
  });

  useEffect(() => {
    urlRef.current = window.location.search;
  }, []);

  useEffect(() => {
    const checkStoredView = () => {
      try {
        const storedView = storeStateKey
          ? getStorage(`DATASOURCE_${storeStateKey}_STATE`, 'user')
          : null;
        const decodedStoredView = storedView ? JSON.parse(atob(storedView)) : null;

        if (decodedStoredView) {
          const { view: viewKey, ...otherViewData } = decodedStoredView ?? {};
          setLastStoredView({
            key: viewKey,
            ...otherViewData,
          });
        } else {
          setLastStoredView(null);
        }
      } catch (err) {
        setLastStoredView(null);
      }
    };

    if (!isEqual(previousStoreStateKey, storeStateKey)) {
      checkStoredView();
    }
  }, [previousStoreStateKey, storeStateKey, getStorage]);

  /**
   * Returns whether the provided module is activated.
   * @param {string} module
   * @returns {boolean}
   */
  const hasModule = useCallback(module => activeModules.includes(module), [activeModules]);

  /**
   * Start a timer to prevent fast concurrent data requests.
   */
  const startDataTimer = useCallback(() => {
    const currentDataTimer = dataTimerRef.current;

    if (currentDataTimer) {
      clearTimeout(currentDataTimer);
    }

    const timer = setTimeout(async () => {
      const { current: currentDataSource } = dataSourceRef;
      const { current: state = {} } = combinedStateRef;
      const { current: currentSettings = {} } = settingsRef;

      if (typeof currentDataSource === 'function') {
        setIsLoadingData(true);

        try {
          const responseData = await currentDataSource({ ...state, settings: currentSettings });

          if (Array.isArray(responseData)) {
            setData(responseData);
          }
        } catch (err) {
          setData([]);
        } finally {
          setIsLoadingData(false);
        }
      } else {
        let newData = Array.isArray(currentDataSource) ? [...currentDataSource] : [];

        if (state.pagination) {
          const { pagination } = state;

          pagination.setItemCount(newData.length);

          newData = [...newData].slice(
            pagination.activePage * pagination.itemsPerPage,
            (pagination.activePage + 1) * pagination.itemsPerPage,
          );
        }

        setData(newData);
        setIsLoadingData(false);
      }
    }, dataSourceTimerTimeout);

    setDataTimer(timer);
  }, [dataSourceTimerTimeout]);

  /**
   * Reload the data source.
   */
  const reloadDataSource = () => {
    startDataTimer();
  };

  const createViewFromUrl = useCallback(() => {
    if (!dataSourceUrl) {
      return null;
    }

    try {
      const url = urlRef.current;
      const urlQuery = parseUrlQuery(url);
      const sourceObject = urlQuery[dataSourceUrlKey];

      if (sourceObject && sourceObject.length > 0) {
        const parsedSourceObject = JSON.parse(atob(sourceObject));
        const { view: viewKey, ...otherViewData } = parsedSourceObject ?? {};

        return {
          key: viewKey,
          ...otherViewData,
        };
      }

      return null;
    } catch (err) {
      return null;
    }
  }, [dataSourceUrl, dataSourceUrlKey]);

  /**
   * Load a certain view and set the view state.
   * @param {object} view
   */
  const loadView = useCallback(view => {
    const state = latestStateRef.current;
    const provided = latestProvidedStateRef?.current;
    const availableProperties = [
      ...(state?.dataProperties?.properties ?? []),
      ...(state?.dataProperties?.customProperties ?? []),
    ].reduce((acc, property) => ({ ...acc, [property.key]: property }), {});

    if (state?.pagination) {
      const isValidItemsPerPage = (state.pagination?.itemsPerPageOptions ?? []).includes(
        view?.itemsPerPage,
      );
      const verifiedItemsPerPage = isValidItemsPerPage
        ? view.itemsPerPage
        : provided?.pagination?.itemsPerPage ?? 25;

      state.pagination.setItemsPerPage(verifiedItemsPerPage);
    }

    if (state?.dataProperties) {
      const verifiedProperties = (view?.properties ?? []).reduce((acc, property) => {
        const availableProperty = availableProperties[property.key];

        if (availableProperty) {
          return [...acc, availableProperty];
        }

        return acc;
      }, []);

      state.dataProperties.setSelectedProperties(verifiedProperties);
      state.dataProperties.setCustomProperties(view?.customProperties ?? []);
    }

    if (state?.filters) {
      const availableFilters = (state.filters?.filters ?? []).reduce(
        (acc, filter) => ({ ...acc, [filter.key]: filter }),
        {},
      );
      const verifiedFilters = (view?.filters ?? [])
        .filter(filter => availableFilters[filter?.key])
        .reduce((acc, filter) => {
          const availableFilter = availableFilters[filter.key] ?? {};
          const { type } = availableFilter;

          return [...acc, { ...filter, type }];
        }, []);

      state.filters.setActiveFilters(verifiedFilters);
    }

    if (state?.sorting) {
      const sortableProperties = Object.keys(availableProperties).reduce((acc, propertyKey) => {
        const property = availableProperties[propertyKey] ?? {};
        const sortKey = property.sortKey || property.key;

        if (sortKey) {
          return { ...acc, [sortKey]: property };
        }

        return acc;
      }, {});
      const verifiedSorting = (view?.sort ?? []).filter(sort => !!sortableProperties[sort.key]);

      state.sorting.setActiveSort(verifiedSorting);
    }
  }, []);

  const viewActions = {
    loadView,
  };
  const viewActionsRef = useRef(viewActions);

  useEffect(() => {
    actionsRef.current = actions;
  });

  useEffect(() => {
    if (!isEqual(options, previousOptions)) {
      setSettings({ ...DATA_SOURCE_DEFAULT_SETTINGS, ...(options ?? {}) });
    }
  }, [options, previousOptions]);

  useEffect(() => {
    if (hasModule('filters') && !isEqual(providedFilters, previousProvidedFilters)) {
      combinedStateRef.current.filters.setFilters(providedFilters);
    }
  }, [hasModule, providedFilters, previousProvidedFilters]);

  useEffect(() => {
    if (hasModule('filters') && !isEqual(providedFixedFilters, previousProvidedFixedFilters)) {
      combinedStateRef.current.filters.setFixedFilters(providedFixedFilters);
    }
  }, [hasModule, providedFixedFilters, previousProvidedFixedFilters]);

  useEffect(() => {
    if (
      hasModule('dataProperties') &&
      !isEqual(providedDataProperties, previousProvidedProperties)
    ) {
      const state = combinedStateRef?.current;

      state.dataProperties.setProperties(providedDataProperties);

      if (hasModule('sorting') && !providedSortOptions) {
        const fixedPropertyKeys = (providedFixedProperties ?? [])
          .filter(fixedProperty => !fixedProperty.isSortable)
          .map(fixedProperty => fixedProperty.key);
        const availableSortOptions = providedDataProperties
          .filter(
            property => !fixedPropertyKeys.includes(property.key) && property.isSortable !== false,
          )
          .reduce(
            (acc, sortOption) => [
              ...acc,
              { ...sortOption, key: sortOption.sortKey || sortOption.key },
            ],
            [],
          );

        state.sorting.setSortOptions(availableSortOptions);
      }
    }
  }, [
    hasModule,
    providedDataProperties,
    previousProvidedProperties,
    providedFixedProperties,
    providedSortOptions,
  ]);

  useEffect(() => {
    if (
      hasModule('dataProperties') &&
      !isEqual(providedFixedProperties, previousProvidedFixedProperties)
    ) {
      combinedStateRef.current.dataProperties.setFixedProperties(providedFixedProperties);
    }
  }, [hasModule, providedFixedProperties, previousProvidedFixedProperties]);

  useEffect(() => {
    if (hasModule('sorting') && !isEqual(providedSortOptions, previousProvidedSortOptions)) {
      combinedStateRef.current.sorting.setSortOptions(providedSortOptions);
    }
  }, [hasModule, providedSortOptions, previousProvidedSortOptions]);

  useEffect(() => {
    if (hasModule('views') && !isEqual(providedFixedViews, previousProvidedFixedViews)) {
      combinedStateRef.current.views.setFixedViews(providedFixedViews);
    }
  }, [hasModule, providedFixedViews, previousProvidedFixedViews]);

  useEffect(() => {
    if (hasModule('views')) {
      const state = combinedStateRef.current;
      const currentActions = actionsRef.current;
      const currentProvidedDefaultView = latestProvidedStateRef?.current?.views?.defaultView;
      const availableViews = (providedViews ?? []).filter(view => view.active === true);
      const availableDefaultView = availableViews.find(view => view.isDefault);
      const viewFromUrl = createViewFromUrl();

      if (
        (!settingsIsLoadingData && !isEqual(providedViews, previousProvidedViews)) ||
        (!settingsIsLoadingData && availableViews?.length > 0 && !viewsInitialized)
      ) {
        const existingView = [...(providedFixedViews ?? []), ...availableViews].find(
          view => view.key === viewFromUrl?.key,
        );
        const existingDecodedStoredView = [...(providedFixedViews ?? []), ...availableViews].find(
          view => view.key === lastStoredView?.key,
        );

        state.views.setViews(availableViews);

        if (existingView) {
          currentActions.views.setActiveView({ ...existingView, ...viewFromUrl });
        } else if (existingDecodedStoredView) {
          currentActions.views.setActiveView({
            ...existingDecodedStoredView,
            ...lastStoredView,
          });
        } else if (!existingDecodedStoredView && lastStoredView) {
          loadView(lastStoredView);
        } else if (availableDefaultView) {
          currentActions.views.setActiveView(availableDefaultView);
        } else {
          loadView(currentProvidedDefaultView);
        }

        setViewsInitialized(true);
      } else if (!settingsIsLoadingData && !viewsInitialized) {
        if (viewFromUrl) {
          loadView(viewFromUrl);
        } else {
          loadView(lastStoredView || currentProvidedDefaultView);
        }

        setViewsInitialized(true);
      }
    }
  }, [
    storeStateKey,
    getStorage,
    lastStoredView,
    createViewFromUrl,
    hasModule,
    loadView,
    providedFixedViews,
    providedViews,
    previousProvidedViews,
    settingsIsLoadingData,
    viewsInitialized,
  ]);

  useEffect(() => {
    dataSourceRef.current = dataSource;
    combinedStateRef.current = combinedState;
    dataTimerRef.current = dataTimer;
    providedStateRef.current = providedState;
    providedLoadFilterOptionsRef.current = providedLoadFilterOptions;
    viewActionsRef.current = viewActions;
    dataRef.current = data;
  });

  /**
   * Set the view information when the active view changes.
   */
  useEffect(() => {
    const state = combinedStateRef?.current;
    const currentViewActions = viewActionsRef?.current;

    if (
      hasModule('views') &&
      !settingsIsLoadingData &&
      viewsInitialized &&
      !isEqual(activeView?.key, previousActiveView?.key)
    ) {
      currentViewActions.loadView(state.views.getActiveView());
    }
  }, [
    hasModule,
    defaultView,
    activeView,
    previousActiveView,
    settingsIsLoadingData,
    filters,
    viewsInitialized,
  ]);

  useEffect(() => {
    activeViewRef.current = activeView;
  });

  useEffect(() => {
    const currentData = dataRef?.current ?? [];
    const previousSelection = selectedItemsRef?.current ?? [];
    const selection = currentData.filter(item => selectedItems.includes(item.id));
    const deselection = currentData.filter(
      item => previousSelection.includes(item.id) && !selectedItems.includes(item.id),
    );
    const { onAddSelection, onRemoveSelection } = providedStateRef?.current?.selection ?? {};

    if (onAddSelection && selection.length > 0) {
      onAddSelection(selection);
    }

    if (onRemoveSelection && deselection.length > 0) {
      onRemoveSelection(deselection);
    }
  }, [selectedItems]);

  useEffect(() => {
    selectedItemsRef.current = selectedItems;
  });

  const dataSourceIsDifferent =
    Array.isArray(dataSource) &&
    dataSourceRef.current &&
    !isEqual(dataSource, dataSourceRef.current);

  /**
   * Load data from the data source if a data dependency changes.
   */
  useEffect(() => {
    if (dataSourceRef?.current && !settingsRef.current?.isLoadingData) {
      startDataTimer();
    }
  }, [
    activePage,
    itemsPerPage,
    activeFilters,
    fixedFilters,
    activeSort,
    startDataTimer,
    dataSourceIsDifferent,
    searchValue,
    isFuzzy,
  ]);

  /**
   * Reset the pagination if a reset pagination dependency changes.
   */
  useEffect(() => {
    if (hasModule('pagination')) {
      const state = combinedStateRef?.current;
      const currentActivePage = state.pagination.activePage ?? 0;

      if (currentActivePage !== 0) {
        state.pagination.resetPagination();
      }
    }
  }, [hasModule, activeFilters, fixedFilters, activeSort, searchValue, isFuzzy]);

  /**
   * Parse the data source url when the dataSourceUrl setting is enabled.
   */
  useEffect(() => {
    const decodeViewFromUrl = () => {
      try {
        const urlView = createViewFromUrl();
        const storedView = storeStateKey
          ? getStorage(`DATASOURCE_${storeStateKey}_STATE`, 'user')
          : null;
        const decodedStoredView = storedView ? JSON.parse(atob(storedView)) : null;

        if (!hasModule('views')) {
          if (urlView) {
            loadView(urlView);
          } else if (decodedStoredView) {
            loadView(decodedStoredView);
          }
        }
      } catch (err) {
        // Do nothing.
      }
    };

    decodeViewFromUrl();
  }, [hasModule, createViewFromUrl, loadView, storeStateKey, getStorage]);

  /**
   * Replace the data source state in the url when the dataSourceUrl setting is enabled.
   */
  useEffect(() => {
    const state = combinedStateRef?.current;
    const stateDataSourceUrl = createDataSourceUrl(state, {
      dataSourceUrlKey,
    });

    if (dataSourceUrl === true) {
      window.history.replaceState(null, null, stateDataSourceUrl);
    }

    if (storeStateKey) {
      setStorage(
        `DATASOURCE_${storeStateKey}_STATE`,
        btoa(JSON.stringify(createDataSourceObject(state))),
        'user',
      );
    }
  }, [
    dataSourceUrl,
    dataSourceUrlKey,
    storeStateKey,
    itemsPerPage,
    activeFilters,
    fixedFilters,
    activeSort,
    activeView,
    selectedProperties,
    setStorage,
  ]);

  /**
   * Replaces the provided load filter options
   * to create a wrapped function which provides the context state as second argument.
   */
  useEffect(() => {
    const state = combinedStateRef?.current;
    const loadFilterOptions = providedLoadFilterOptionsRef?.current;

    if (hasModule('filters') && loadFilterOptions) {
      state.filters.setLoadFilterOptions(async filter => loadFilterOptions(filter, state));
    }
  }, [hasModule, activePage, itemsPerPage, activeFilters, activeSort, filters]);

  /* Return all data source state and actions */
  return {
    ...combinedState,
    hasModule,
    reloadDataSource,
    data,
    setData,
    isLoadingData: isLoadingData || settings?.isLoadingData === true,
    setIsLoadingData,
    settings,
    setSettings,
    ...(hasModule('views') ? viewActions : {}),
  };
};
